/**
 * @module components/capabilities
 *
 * The twelve enterprise capabilities, grouped into design / deliver / sustain,
 * beside a radial capability web that places them around the ADM hub.
 */

import { MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa } from "../core/dom.js";
import { watchReveal } from "../animations/scrollReveal.js";
import { CAPABILITIES, getCapabilitiesByGroup } from "../data/capabilities.js";
import { getPhaseById } from "../data/phases.js";
import { icon } from "../svg/icons.js";
import { capabilityWeb } from "../svg/scenes.js";

/**
 * Render the maturity meter.
 *
 * @param {number} value 1–5
 * @returns {string} Markup.
 */
function maturityMarkup(value) {
  const pips = Array.from({ length: 5 }, (_, i) => `<i class="maturity__pip${i < value ? " is-on" : ""}"></i>`).join("");
  return `
    <span class="maturity" role="img" aria-label="Typical starting maturity ${value} out of 5">
      <span class="maturity__track" aria-hidden="true">${pips}</span>
      <span class="maturity__label" aria-hidden="true">start ${value}/5</span>
    </span>`;
}

/**
 * Render one capability card.
 *
 * @param {import("../data/capabilities.js").Capability} capability
 * @returns {string} Markup.
 */
function capabilityMarkup(capability) {
  const phases = capability.phases
    .map((id) => getPhaseById(id)?.code)
    .filter(Boolean)
    .map((code) => `<li class="pill">${code}</li>`)
    .join("");

  return `
    <li class="cap-card card" data-capability="${capability.id}">
      <div class="cap-card__top">
        <span class="cap-card__glyph" aria-hidden="true">${icon(capability.glyph, { size: 20 })}</span>
        ${maturityMarkup(capability.maturity)}
      </div>
      <h4 class="cap-card__name">${capability.name}</h4>
      <p class="cap-card__tagline">${capability.tagline}</p>
      <p class="cap-card__body">${capability.body}</p>
      <p class="card__outcome">${icon("target", { size: 16 })}<span>${capability.outcome}</span></p>
      <ul class="cap-card__pills" aria-label="Most active in ADM phases">${phases}</ul>
    </li>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initCapabilities() {
  const root = qs("[data-capabilities]");
  if (!root) return () => {};

  /* --- Scene ------------------------------------------------------------- */

  const scene = qs("[data-cap-scene]", root);
  if (scene) {
    scene.innerHTML = capabilityWeb({
      nodes: CAPABILITIES.map((capability) => ({ id: capability.id, name: capability.short })),
      groups: ["Design", "Deliver", "Sustain"],
      accent: "#5fd4c4",
      secondary: "#d8a657",
    });
  }

  /* --- Grouped grid ------------------------------------------------------ */

  const groupArea = qs("[data-cap-groups]", root);
  if (groupArea) {
    groupArea.innerHTML = getCapabilitiesByGroup()
      .map(
        (group) => `
        <section class="cap-group" data-cap-group="${group.id}" aria-labelledby="cap-group-${group.id}">
          <header class="cap-group__head">
            <h3 class="cap-group__label" id="cap-group-${group.id}">${group.label}</h3>
            <p class="cap-group__body">${group.body}</p>
            <span class="cap-group__count">${String(group.items.length).padStart(2, "0")} capabilities</span>
          </header>
          <ul class="cap-grid">${group.items.map(capabilityMarkup).join("")}</ul>
        </section>`
      )
      .join("");
  }

  /* --- Motion ------------------------------------------------------------ */

  const mm = createMotionContext();

  mm.add(MEDIA.reduce, () => {});

  mm.add(MEDIA.any, () => {
    const cleanup = [];

    if (scene) {
      const spokes = qsa(".web-spokes line", scene);
      const nodes = qsa(".web-node", scene);
      const rings = qsa(".web-ring", scene);

      if (rings.length) {
        cleanup.push(
          watchReveal(rings, {
            from: { drawSVG: "0%" },
            to: { drawSVG: "100%" },
            duration: 1.6,
            ease: "power2.inOut",
            stagger: 0.12,
            start: "top 84%",
          })
        );
      }

      if (spokes.length) {
        cleanup.push(
          watchReveal(spokes, {
            from: { drawSVG: "0%" },
            to: { drawSVG: "100%" },
            duration: 1,
            stagger: 0.04,
            start: "top 84%",
          })
        );
      }

      if (nodes.length) {
        cleanup.push(
          watchReveal(nodes, {
            from: { autoAlpha: 0, scale: 0.5 },
            to: { autoAlpha: 1, scale: 1 },
            duration: 0.6,
            ease: "back.out(1.5)",
            stagger: 0.05,
            start: "top 84%",
          })
        );
      }
    }

    const cards = qsa(".cap-card", root);
    if (cards.length) {
      cleanup.push(
        watchReveal(cards, {
          from: { autoAlpha: 0, y: 20 },
          duration: 0.6,
          stagger: 0.035,
          start: "top 82%",
        })
      );
    }

    return () => cleanup.forEach((dispose) => dispose());
  });

  return () => mm.revert();
}
